import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useQuery } from '@apollo/client'
import { Box, Button, Form, Text } from '@factorialco/deprecated-design-system'
import { RateConfigurationsDatatableDocument } from 'generated/resources/datatable.graphql'
import {
  RateConfigurationAssigneesDataDocument,
  RateConfigurationFormFragment,
  UpdateRateConfigurationDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'
import {
  ExpensableRateConfigurationResourceType,
  ExpensableRateConfigurationResourceTypeEnum,
} from 'modules/expenses/types/Expensable'

interface FormValues {
  employeeSelectionError?: string
}

interface Props {
  title: string
  description: string
  rateConfiguration: RateConfigurationFormFragment
  resourceType: ExpensableRateConfigurationResourceType
  onClose: () => void
}

const AssigneesModal = ({
  title,
  description,
  rateConfiguration,
  onClose,
  resourceType,
}: Props) => {
  const { company } = useCurrentSession()
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const [updateRateConfiguration] = useMutation(UpdateRateConfigurationDocument, {
    extractorFn: (data) => data.expensesMutations.updateExpensesRateConfiguration,
    awaitRefetchQueries: true,
    fetchPolicy: 'no-cache',
    refetchQueries: [RateConfigurationsDatatableDocument],
  })

  const { loading: loadingEmployeesData, data: employeesData } = useQuery(
    RateConfigurationAssigneesDataDocument,
    {
      variables: {
        accessIds: rateConfiguration?.accessIds ?? [],
      },
    }
  )

  const [employeeSelection, setEmployeeSelection] = useState<EmployeeSelectorV2MultiSelectState>({
    type: 'manual',
    ids: [],
  })

  useEffect(() => {
    if (!loadingEmployeesData && (rateConfiguration?.accessIds ?? []).length > 0) {
      const employeeIds =
        employeesData?.employees.employeeNamesConnection.nodes.map((e) => e.id) ?? []
      setEmployeeSelection({ type: 'manual', ids: employeeIds })
    }
  }, [loadingEmployeesData, employeesData, rateConfiguration?.accessIds])

  const form = useForm<FormValues>({
    validateOnChange: false,
    defaultValues: {},
    validate: () => {
      const errors: FormErrors<FormValues> = {}

      if (employeeSelection.type === 'manual' && employeeSelection.ids.length === 0) {
        errors.employeeSelectionError = i18n.t(
          'expenses_v2.settings.rate_configurations.detail.assignees_modal.errors.assignees_empty'
        )
      }
      return errors
    },
    onSubmit: async (_, { setErrors }) => {
      const variables = {
        name: rateConfiguration.name,
        currency: rateConfiguration.currency,
        companyId: company.id,
        legalEntityIds: rateConfiguration.legalEntityIds,
        amountCents: rateConfiguration.amountCents,
        rate: rateConfiguration.rate,
        employeeSelection: employeeSelection,
      }

      const result = await updateRateConfiguration({
        variables: { ...variables, id: rateConfiguration.id },
      })

      const handled = handleResult(result, (errors) => {
        setErrors(errors)
      })
      if (!handled.success) return handled
      handleSuccess(handled.success)
      return handled
    },
  })

  const handleSuccess = (success: boolean) => {
    if (success) {
      app.setFlash({ type: 'correct', message: i18n.t('common.saved_successfully') })

      const redirectPath =
        resourceType === ExpensableRateConfigurationResourceTypeEnum.PerDiem
          ? navigation.settings.expenses.rateConfigurations.fullPath()
          : navigation.settings.expenses.mileageRates.fullPath()

      navigate(redirectPath)
    } else {
      app.setFlash({ type: 'wrong', message: i18n.t('errors.generic_saving') })
    }
  }

  return (
    <Modal
      illustration="document-edit"
      title={title}
      description={description}
      onClose={onClose}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.close')}
          </Button>
          <Button onClick={() => form.handleSubmit()} disabled={form.isSubmitting}>
            {i18n.t('common.save')}
          </Button>
        </>
      }
    >
      <Form padding="s0">
        <EmployeeSelectorV2
          multiple
          value={employeeSelection.type === 'manual' ? employeeSelection.ids : undefined}
          onChange={setEmployeeSelection}
        />
        {form.errors.employeeSelectionError && (
          <Box alignItems="flexStart">
            <Text.Normal size="200" color="radicalRed1000" capitalize>
              {form.errors.employeeSelectionError}
            </Text.Normal>
          </Box>
        )}
      </Form>
    </Modal>
  )
}

export default AssigneesModal
